import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "aiva_dark_mode";

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

interface ThemeCtx {
  readonly darkMode: boolean;
  readonly setDarkMode: (next: boolean) => void;
}

const Ctx = createContext<ThemeCtx>({ darkMode: false, setDarkMode: () => {} });

export function useTheme(): ThemeCtx {
  return useContext(Ctx);
}

interface Props {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: Readonly<Props>) {
  const [darkMode, setDarkMode] = useState<boolean>(readInitial);

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? "dark" : "light";
    try {
      localStorage.setItem(STORAGE_KEY, darkMode ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
  }, [darkMode]);

  const value = useMemo(() => ({ darkMode, setDarkMode }), [darkMode]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
